class MersenneTwister {
  constructor(seed) {
    this.mt = new Array(624);
    this.index = 0;
    this.init(seed);
  }

  init(seed) {
    this.mt[0] = seed >>> 0; // Ensure it's a 32-bit unsigned integer
    for (let i = 1; i < 624; i++) {
      this.mt[i] = (0x6c078965 * (this.mt[i - 1] ^ (this.mt[i - 1] >>> 30)) + i) >>> 0;
    }
  }

  extractNumber() {
    if (this.index === 0) this.twist();
    let y = this.mt[this.index];
    y ^= (y >>> 11);
    y ^= (y << 7) & 0x9d2c5680;
    y ^= (y << 15) & 0xefc60000;
    y ^= (y >>> 18);
    this.index = (this.index + 1) % 624;
    return y >>> 0;
  }

  twist() {
    for (let i = 0; i < 624; i++) {
      let y = (this.mt[i] & 0x80000000) | (this.mt[(i + 1) % 624] & 0x7fffffff);
      this.mt[i] = this.mt[(i + 397) % 624] ^ (y >>> 1);
      if (y % 2 !== 0) {
        this.mt[i] ^= 0x9908b0df;
      }
    }
  }
}

// 自定义函数：生成均匀分布的整数
class UniformIntGenerator {
  constructor(rng, min, max) {
    this.rng = rng;
    this.min = min;
    this.max = max;
  }

  generate() {
    return this.min + (this.rng.extractNumber() % (this.max - this.min + 1));
  }
}

// 使用 MersenneTwister 随机数生成器
const mt = new MersenneTwister(12345); // 传入一个种子值
const uniformIntGen = new UniformIntGenerator(mt, 1, 10); // 创建一个生成 1 到 10 之间整数的生成器

// 生成并打印 5 个随机整数
for (let i = 0; i < 5; i++) {
  console.log(uniformIntGen.generate());
}

export class SampleConsensusModel {
  constructor(cloud = null, isRandom = false) {
    this.input_ = cloud || [];
    this.indices_ = [];
    this.shuffledIndices_ = [];
    this.radiusMin_ = -Number.MAX_VALUE;
    this.radiusMax_ = Number.MAX_VALUE;
    this.samplesRadius_ = 0;
    this.samplesRadiusSearch_ = null;
    this.rngDist_ = { min: 0, max: Number.MAX_SAFE_INTEGER };
    this.customModelConstraints_ = (modelCoefficients) => true; // default constraint function
    this.error_sqr_dists_ = [];
    this.sampleSize_ = 0;
    this.modelSize_ = 0;
    this.max_sample_checks_ = 1000;

    this.setInputCloud(cloud);

    {

      console.log("randomLib:value=", randomLib.randomInt(1, Number.MAX_SAFE_INTEGER));
      this.rng_alg_ = randomLib.MersenneTwister19937;


    }
    // 创建一个均匀分布的整数生成器，范围为 [0, max_int]，类似于 boost::uniform_int<>
    this.rng_dist_ = (min, max) => integer(0, Number.MAX_SAFE_INTEGER)(this.rng_alg_);

    // 创建一个随机数生成器对象
    this.rng_gen_ = this.rng_dist_;
  }
  // 生成一个随机数
  generateRandom() {
    let a = randomLib.randomInt(0, 2147483647);
    return a;
  }
  setInputCloud(cloud) {
    this.input_ = cloud;
    if (this.indices_.length === 0) {
      this.indices_ = Array.from({ length: cloud.length }, (_, i) => i);
    }
    this.shuffledIndices_ = [...this.indices_];
  }

  getInputCloud() {
    return this.input_;
  }

  setIndices(indices) {
    this.indices_ = indices;
    this.shuffledIndices_ = [...indices];
  }

  getIndices() {
    return this.indices_;
  }

  getModelType() {
    // To be implemented in subclasses
    throw new Error("getModelType method must be implemented by subclass");
  }

  getClassName() {
    return this.modelName_;
  }

  getSampleSize() {
    return this.sampleSize_;
  }

  getModelSize() {
    return this.modelSize_;
  }

  setRadiusLimits(minRadius, maxRadius) {
    this.radiusMin_ = minRadius;
    this.radiusMax_ = maxRadius;
  }

  getRadiusLimits() {
    return { min: this.radiusMin_, max: this.radiusMax_ };
  }

  setModelConstraints(func) {
    if (typeof func !== "function") {
      console.error("The given function is not callable!");
      return;
    }
    this.customModelConstraints_ = func;
  }

  setSamplesMaxDist(radius, search) {
    this.samplesRadius_ = radius;
    this.samplesRadiusSearch_ = search;
  }

  getSamplesMaxDist() {
    return this.samplesRadius_;
  }

  drawIndexSample(sample) {
    const sampleSize = sample.length;

    const indexSize = this.shuffledIndices_.length;

    for (let i = 0; i < sampleSize; i++) {
      const r = this.generateRandom();
      const randIndex = i + (r % (indexSize - i));
      [this.shuffledIndices_[i], this.shuffledIndices_[randIndex]] = [this.shuffledIndices_[randIndex], this.shuffledIndices_[i]];
    }

    const sampleSize2 = Math.min(this.shuffledIndices_.length, sample.length);

    for (let i = 0; i < sampleSize2; i++) {
      sample[i] = this.shuffledIndices_[i];
    }
  }

  drawIndexSampleRadius(sample) {
    const sampleSize = sample.length;
    const indexSize = this.shuffledIndices_.length;

    const randIndex = Math.floor(Math.random() * indexSize);
    [this.shuffledIndices_[0], this.shuffledIndices_[randIndex]] = [this.shuffledIndices_[randIndex], this.shuffledIndices_[0]];

    const indices = [];
    const sqrDists = [];

    // Assume samplesRadiusSearch_.radiusSearch function is available in search
    this.samplesRadiusSearch_.radiusSearch(this.input_[this.shuffledIndices_[0]], this.samplesRadius_, indices, sqrDists);

    if (indices.length < sampleSize - 1) {
      for (let i = 1; i < sampleSize; i++) {
        this.shuffledIndices_[i] = this.shuffledIndices_[0];
      }
    } else {
      for (let i = 0; i < sampleSize - 1; i++) {
        const randIndex = Math.floor(Math.random() * indices.length);
        [indices[i], indices[randIndex]] = [indices[randIndex], indices[i]];
      }
      for (let i = 1; i < sampleSize; i++) {
        this.shuffledIndices_[i] = indices[i - 1];
      }
    }

    sample.push(...this.shuffledIndices_.slice(0, sampleSize));
  }

  // computeVariance(errorSqrDists) {
  //   const dists = [...errorSqrDists];
  //   const medIdx = Math.floor(dists.length / 2);
  //   dists.sort((a, b) => a - b);
  //   const medianErrorSqr = dists[medIdx];
  //   return 2.1981 * medianErrorSqr;
  // }
  computeVarianceFromDists(error_sqr_dists) {
    // 复制输入数组
    let dists = [...error_sqr_dists];

    // 排序并找到中位数的索引
    const medIdx = Math.floor(dists.length / 2);

    // 使用快速选择算法找到中位数
    dists.sort((a, b) => a - b);  // 在 JavaScript 中可以直接使用 sort 对数组排序

    const median_error_sqr = dists[medIdx];

    // 返回计算后的方差值
    return 2.1981 * median_error_sqr;
  }

  computeVariance() {
    if (this.error_sqr_dists_.length === 0) {
      console.error("[pcl::SampleConsensusModel::computeVariance] The variance of the Sample Consensus model distances cannot be estimated, as the model has not been computed yet. Please compute the model first or at least run selectWithinDistance before continuing. Returning NaN!");
      return NaN;
    }

    return this.computeVarianceFromDists(this.error_sqr_dists_);
  }

  computeVarianceFromModel() {
    if (this.errorSqrDists_.length === 0) {
      console.error("The variance of the Sample Consensus model distances cannot be estimated. Model has not been computed yet.");
      return NaN;
    }
    return this.computeVariance(this.errorSqrDists_);
  }

  // These are placeholder methods and need to be overridden in subclasses.
  // getSamples(iterations, samples) {
  //   throw new Error("getSamples must be implemented by subclass");
  // }
  getSamples(iterations, samples) {
    // We're assuming that indices_ have already been set in the constructor
    if (this.indices_.length < this.getSampleSize()) {
      console.error(`[SampleConsensusModel::getSamples] Cannot select ${samples.length} unique points out of ${indices_.length}!`);
      // one of these will make it stop :)
      samples.length = 0;
      iterations = Number.MAX_SAFE_INTEGER - 1;
      return;
    }

    // Get a second point which is different than the first
    samples.length = this.getSampleSize();
    for (let iter = 0; iter < this.max_sample_checks_; ++iter) {
      // Choose the random indices
      if (this.samplesRadius_ < Number.EPSILON) {
        this.drawIndexSample(samples);
      } else {
        this.drawIndexSampleRadius(samples);
      }

      // If it's a good sample, stop here
      if (this.isSampleGood(samples)) {
        console.debug(`[SampleConsensusModel::getSamples] Selected ${samples.length} samples.`);
        return;
      }
    }
    // console.debug(`[SampleConsensusModel::getSamples] WARNING: Could not select ${this.getSampleSize()} sample points in ${this.maxSampleChecks_} iterations!`);
    samples.length = 0;
  }

  computeModelCoefficients(samples, modelCoefficients) {
    throw new Error("computeModelCoefficients must be implemented by subclass");
  }

  optimizeModelCoefficients(inliers, modelCoefficients, optimizedCoefficients) {
    throw new Error("optimizeModelCoefficients must be implemented by subclass");
  }

  getDistancesToModel(modelCoefficients, distances) {
    throw new Error("getDistancesToModel must be implemented by subclass");
  }

  selectWithinDistance(modelCoefficients, threshold, inliers) {
    throw new Error("selectWithinDistance must be implemented by subclass");
  }

  countWithinDistance(modelCoefficients, threshold) {
    throw new Error("countWithinDistance must be implemented by subclass");
  }

  projectPoints(inliers, modelCoefficients, projectedPoints, copyDataFields = true) {
    throw new Error("projectPoints must be implemented by subclass");
  }

  doSamplesVerifyModel(indices, modelCoefficients, threshold) {
    throw new Error("doSamplesVerifyModel must be implemented by subclass");
  }

  isSampleGood(samples) {
    throw new Error("isSampleGood must be implemented by subclass");
  }
}
export class SampleConsensusModelFromNormals {
  constructor() {
    // PointCloudNConstPtr would be just a reference to the normals in JavaScript
    this.normals = null;
    this.normalDistanceWeight = 0.0;
  }

  /**
   * Set the normal angular distance weight.
   * @param {number} w The relative weight (between 0 and 1) to give to the angular
   * distance (0 to pi/2) between point normals and the plane normal.
   */
  setNormalDistanceWeight(w) {
    if (w < 0.0 || w > 1.0) {
      console.error("[SampleConsensusModel::setNormalDistanceWeight] w is " + w + ", but should be in [0; 1]. Weight will not be set.");
      return;
    }
    this.normalDistanceWeight = w;
  }

  /** Get the normal angular distance weight. */
  getNormalDistanceWeight() {
    return this.normalDistanceWeight;
  }

  /**
   * Provide a pointer to the input dataset that contains the point
   * normals of the XYZ dataset.
   * @param {Array} normals The input normals array (PointCloudNConstPtr equivalent)
   */
  setInputNormals(normals) {
    this.normals = normals;
  }

  /** Get a pointer to the normals of the input XYZ point cloud dataset. */
  getInputNormals() {
    return this.normals;
  }
}

export class Functor {
  constructor(m_data_points = 0) {
    this.m_data_points_ = m_data_points;
  }

  /** Get the number of values. */
  values() {
    return this.m_data_points_;
  }
}


